import React, { useState, useEffect, useRef } from 'react';
import { Send, Bot, User, Info, AlertTriangle, CheckCircle, FileText, RefreshCw, Flag, ChevronsLeft, ChevronsRight, X } from 'lucide-react';
import ReactMarkdown from 'react-markdown';
import { askBizChat } from '../../api/bizchatApi';

type ChatMsg = { role: 'user' | 'ai'; text: string };

type SummaryData = {
    documentation: { status: 'ok' | 'blocked'; text: string; icon: string };
    risks: { status: 'ok' | 'warning'; text: string; icon: string };
    readiness: { status: 'ready' | 'not_ready'; text: string; icon: string };
    actions: { label: string; type: 'flag' | 'request' | 'move' }[];
};

interface DealChatbotProps {
    businessSlug: string | null;
    isMaximized?: boolean;
    onToggleMaximize?: () => void;
    onClose?: () => void;
}

const DealChatbot: React.FC<DealChatbotProps> = ({ businessSlug, isMaximized = false, onToggleMaximize, onClose }) => {
    // Chat State
    const [chatMessages, setChatMessages] = useState<ChatMsg[]>([]);
    const [chatInput, setChatInput] = useState('');
    const [chatLoading, setChatLoading] = useState(false);
    const [summaryData, setSummaryData] = useState<SummaryData | null>(null);
    const chatScrollRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        if (!businessSlug) return;
        // Reset chat and fetch summary on business change
        setChatMessages([]);
        setSummaryData(null);
        fetchchatSummary(businessSlug);
    }, [businessSlug]);

    useEffect(() => {
        if (chatScrollRef.current) {
            chatScrollRef.current.scrollTop = chatScrollRef.current.scrollHeight;
        }
    }, [chatMessages, summaryData]);

    const fetchchatSummary = async (slug: string) => {
        setChatMessages([]);
        setSummaryData(null);
        setChatLoading(true);
        try {
            // Ask for structured summary
            const response = await askBizChat({
                business_slug: slug,
                question: "__initial_summary__"
            });

            // Allow for backend to return either JSON (new) or text (old fallback)
            try {
                const parsed = JSON.parse(response.answer);
                setSummaryData(parsed);
            } catch (e) {
                // Fallback to plain text message if parsing fails
                setChatMessages([{ role: 'ai', text: response.answer }]);
            }
        } catch (err) {
            console.error("Failed to fetch summary:", err);
            setChatMessages([{ role: 'ai', text: "Ready to help. Ask me anything about this deal." }]);
        } finally {
            setChatLoading(false);
        }
    };

    const handleChatSubmit = async (overrideText?: string) => {
        const question = overrideText || chatInput.trim();
        if (!question || chatLoading || !businessSlug) return;

        setChatInput('');
        setChatMessages(prev => [...prev, { role: 'user', text: question }]);
        setChatLoading(true);

        // If we were showing summary, now we switch to chat view naturally
        // as chatMessages will no longer be empty

        try {
            const response = await askBizChat({ business_slug: businessSlug, question });
            setChatMessages(prev => [...prev, { role: 'ai', text: response.answer }]);
        } catch (err) {
            setChatMessages(prev => [...prev, { role: 'ai', text: "Sorry, I encountered an error answering that." }]);
        } finally {
            setChatLoading(false);
        }
    };

    // Helper to render icons based on string
    const StatusIcon = ({ status, type }: { status: string, type: 'doc' | 'risk' | 'ready' }) => {
        if (type === 'doc') return <Info className="w-5 h-5 text-gray-400 shrink-0 mt-0.5" />;
        if (type === 'risk') return <AlertTriangle className="w-5 h-5 text-gray-400 shrink-0 mt-0.5" />;
        if (type === 'ready') return <CheckCircle className="w-5 h-5 text-green-500 shrink-0 mt-0.5" />;
        return null;
    };

    const ActionIcon = ({ type }: { type: string }) => {
        if (type === 'request') return <div className="w-6 h-6 rounded bg-blue-50 flex items-center justify-center text-blue-600"><FileText className="w-3.5 h-3.5" /></div>;
        if (type === 'move') return <div className="w-6 h-6 rounded bg-purple-50 flex items-center justify-center text-purple-600"><RefreshCw className="w-3.5 h-3.5" /></div>;
        if (type === 'flag') return <div className="w-6 h-6 rounded bg-red-50 flex items-center justify-center text-red-600"><Flag className="w-3.5 h-3.5" /></div>;
        return <div className="w-6 h-6 rounded bg-gray-100" />;
    };

    const showSummary = chatMessages.length === 0 && summaryData;

    return (
        <div className={`${isMaximized ? 'w-[800px]' : 'w-[400px]'} bg-white shadow-2xl border-l border-gray-200 h-full overflow-hidden shrink-0 flex flex-col transition-all duration-300 ease-in-out`}>
            <div className="flex justify-between items-center px-6 py-4 bg-white border-b border-gray-100 shrink-0">
                <div className="flex items-center gap-3">
                    {onToggleMaximize && (
                        <button
                            onClick={onToggleMaximize}
                            className="p-1 rounded hover:bg-gray-100 text-gray-400 hover:text-gray-600 transition-colors"
                            title={isMaximized ? "Collapse" : "Expand"}
                        >
                            {isMaximized ? <ChevronsRight className="w-5 h-5" /> : <ChevronsLeft className="w-5 h-5" />}
                        </button>
                    )}
                    <h2 className="text-lg font-bold text-gray-900 leading-none">Finz AI Insights</h2>
                </div>
                <div className="flex items-center gap-3">
                    <button
                        onClick={() => businessSlug && fetchchatSummary(businessSlug)}
                        className="p-1 rounded hover:bg-gray-100 text-gray-400 hover:text-gray-600 transition-colors"
                        title="Refresh Insights"
                    >
                        <RefreshCw className={`w-4 h-4 ${chatLoading ? 'animate-spin' : ''}`} />
                    </button>
                    {onClose && (
                        <button
                            onClick={onClose}
                            className="p-1 rounded hover:bg-gray-100 text-gray-400 hover:text-gray-600 transition-colors"
                            title="Close"
                        >
                            <X className="w-5 h-5" />
                        </button>
                    )}
                </div>
            </div>

            <div
                ref={chatScrollRef}
                className="flex-1 overflow-y-auto px-6 py-2 space-y-4"
            >
                {/* Loading State */}
                {chatMessages.length === 0 && !summaryData && chatLoading && (
                    <div className="flex items-center gap-2 text-sm text-gray-500 animate-pulse pt-4">
                        <Bot className="w-4 h-4" />
                        Analyzing deal data...
                    </div>
                )}

                {/* Summary View (Show if no chat messages yet) */}
                {showSummary && summaryData && (
                    <div className="animate-in fade-in slide-in-from-bottom-2 duration-500 space-y-8 pt-2">

                        {/* Summary Section */}
                        <div className="space-y-4">
                            <h3 className="text-xs font-bold text-gray-900 uppercase tracking-wider">SUMMARY</h3>

                            <div className="flex gap-3">
                                <StatusIcon status={summaryData.documentation.status} type="doc" />
                                <div>
                                    <div className="text-sm font-semibold text-gray-700">Documentation</div>
                                    <p className="text-sm text-gray-500 leading-relaxed mt-0.5">{summaryData.documentation.text}</p>
                                </div>
                            </div>

                            <div className="flex gap-3">
                                <StatusIcon status={summaryData.risks.status} type="risk" />
                                <div>
                                    <div className="text-sm font-semibold text-gray-700">Risk Signals</div>
                                    <p className="text-sm text-gray-500 leading-relaxed mt-0.5">{summaryData.risks.text}</p>
                                </div>
                            </div>

                            <div className="flex gap-3">
                                <StatusIcon status={summaryData.readiness.status} type="ready" />
                                <div>
                                    <div className="text-sm font-semibold text-gray-700">Approval Readiness</div>
                                    <p className="text-sm text-gray-500 leading-relaxed mt-0.5">{summaryData.readiness.text}</p>
                                </div>
                            </div>
                        </div>

                        {/* Recommended Actions */}
                        <div className="space-y-4 pb-4">
                            <h3 className="text-xs font-bold text-gray-900 uppercase tracking-wider">RECOMMENDED ACTIONS</h3>
                            {summaryData.actions.map((act, i) => (
                                <div
                                    key={i}
                                    onClick={() => handleChatSubmit(`Action: ${act.label}`)}
                                    className="flex gap-3 items-start group cursor-pointer hover:bg-gray-50 -mx-2 p-2 rounded transition-colors"
                                >
                                    <ActionIcon type={act.type} />
                                    <span className="text-sm text-gray-700 font-medium mt-0.5 leading-snug">
                                        {act.label}
                                        {act.type === 'move' && <span className="inline-block bg-yellow-100 text-yellow-800 text-[10px] font-bold px-1.5 py-0.5 rounded ml-2 align-middle uppercase">In Review</span>}
                                    </span>
                                </div>
                            ))}
                        </div>
                    </div>
                )}

                {/* Chat Messages (Show if user chats) */}
                {chatMessages.map((msg, i) => (
                    <div key={i} className={`flex gap-3 ${msg.role === 'user' ? 'flex-row-reverse' : ''}`}>
                        <div className={`w-8 h-8 rounded-full flex items-center justify-center shrink-0 ${msg.role === 'ai' ? 'bg-blue-100 text-blue-600' : 'bg-gray-200 text-gray-600'}`}>
                            {msg.role === 'ai' ? <Bot className="w-5 h-5" /> : <User className="w-5 h-5" />}
                        </div>
                        <div className={`rounded-lg p-3 text-sm max-w-[85%] ${msg.role === 'ai'
                            ? 'bg-blue-50 text-gray-800'
                            : 'bg-gray-900 text-white'
                            }`}>
                            {msg.role === 'ai' ? (
                                <ReactMarkdown
                                    components={{
                                        ul: ({ ...props }) => <ul className="list-disc pl-4 my-2 space-y-1" {...props} />,
                                        ol: ({ ...props }) => <ol className="list-decimal pl-4 my-2 space-y-1" {...props} />,
                                        li: ({ ...props }) => <li className="leading-relaxed" {...props} />,
                                        p: ({ ...props }) => <p className="mb-2 last:mb-0 leading-relaxed" {...props} />,
                                        strong: ({ ...props }) => <span className="font-semibold text-gray-900" {...props} />
                                    }}
                                >
                                    {msg.text}
                                </ReactMarkdown>
                            ) : (
                                msg.text
                            )}
                        </div>
                    </div>
                ))}

                {chatLoading && chatMessages.length > 0 && (
                    <div className="flex items-center gap-2 text-sm text-gray-500 animate-pulse ml-11">
                        Thinking...
                    </div>
                )}
            </div>

            {/* Input Area */}
            <div className="p-4 border-t border-gray-100 bg-white shrinking-0">
                <div className="relative mb-3">
                    <input
                        type="text"
                        placeholder="Type a message..."
                        className="w-full text-sm border border-gray-300 rounded-lg px-3 py-2.5 pr-10 focus:outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500 transition-all placeholder:text-gray-400"
                        value={chatInput}
                        onChange={(e) => setChatInput(e.target.value)}
                        onKeyDown={(e) => e.key === 'Enter' && handleChatSubmit()}
                        disabled={chatLoading}
                    />
                    <button
                        onClick={() => handleChatSubmit()}
                        disabled={chatLoading}
                        className={`absolute right-2 top-2 p-1 transition-colors ${chatLoading || !chatInput.trim() ? 'text-gray-300' : 'text-blue-600 hover:text-blue-700'}`}
                    >
                        <Send className="w-4 h-4" />
                    </button>
                </div>
                {/* Quick Action Pills */}
                <div className="flex flex-wrap gap-2">
                    <button
                        onClick={() => {
                            setSummaryData(null);
                            setChatMessages([]);
                            fetchchatSummary(businessSlug || "");
                        }}
                        disabled={chatLoading}
                        className="px-3 py-1 text-xs font-medium bg-white hover:bg-gray-50 text-gray-600 rounded-full border border-gray-200 transition-all shadow-sm hover:shadow"
                    >
                        Summarize
                    </button>
                    {["What's missing", 'Draft terms'].map(tag => (
                        <button
                            key={tag}
                            onClick={() => handleChatSubmit(tag)}
                            disabled={chatLoading}
                            className="px-3 py-1 text-xs font-medium bg-white hover:bg-gray-50 text-gray-600 rounded-full border border-gray-200 transition-all shadow-sm hover:shadow"
                        >
                            {tag}
                        </button>
                    ))}
                </div>
            </div>
        </div >
    );
};

export default DealChatbot;
